import { FC } from "react";
import { Text, types } from "react-bricks/frontend";
import { color, fonts } from "@styles";
import { useLocale } from "@hooks";
import styled from "styled-components";
import { Tooltip } from "components/atoms/Tooltip";

export interface StatsBarItemProps {
  tooltip?: string;
  tooltipDesktopWidth?: string;
  marginLeft?: string;
  marginRight?: string;
  bold?: boolean;
  statColor?: string;
  textColor?: string;
  maxWidth?: string;
  hasStats?: boolean;
  bodyTextSize?: string;
}

export const StatsBarItem: types.Brick<StatsBarItemProps> = ({
  tooltip,
  tooltipDesktopWidth = "18.75rem",
  marginLeft = "1.5rem",
  marginRight,
  bold = false,
  maxWidth = "9.375rem",
  statColor = color.white,
  textColor = color.white,
  hasStats,
  bodyTextSize,
}) => {
  const { locale } = useLocale();
  const isNotBold = !bold;

  return (
    <StatContainer>
      {hasStats && (
        <StatValue color={statColor}>
          <Text
            propName="value"
            placeholder="85%"
            renderBlock={({ children }) => <>{children}</>}
          />
        </StatValue>
      )}
      <Description
        marginLeft={marginLeft}
        marginRight={marginRight}
        weight={isNotBold}
        maxWidth={hasStats ? maxWidth : "none"}
        align={hasStats ? "left" : "center"}
        locale={locale}
        textColor={textColor}
        fontSize={bodyTextSize}
      >
        <span>
          <Text
            propName="description"
            placeholder="Enter stat description"
            multiline={true}
            renderBlock={({ children }) => (
              <ToolTipWrap>
                {children}
                {tooltip && (
                  <Tooltip
                    inverted={textColor !== color.white}
                    tooltipDesktopWidth={tooltipDesktopWidth}
                  >
                    {tooltip}
                  </Tooltip>
                )}
              </ToolTipWrap>
            )}
          />
        </span>
      </Description>
    </StatContainer>
  );
};

StatsBarItem.schema = {
  name: "stats-bar-block",
  label: "Stats Block",
  category: "Stats Components",
  hideFromAddMenu: true,
  // Define default props
  getDefaultProps: () => ({
    value: "85%",
    description: "of employees say YuLife engages them better than any other benefit",
    bold: false,
  }),
  // Define side edit props
  sideEditProps: [
    {
      name: "bold",
      label: "Bold Description",
      type: types.SideEditPropType.Boolean,
    },
    {
      name: "tooltip",
      label: "Tooltip text",
      type: types.SideEditPropType.Text,
    },
  ],
};

const TIGHT_MOBILE_BREAKPOINT = `@media only screen and (max-width: 27.5rem)`;

const StatContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1.5rem;

  ${TIGHT_MOBILE_BREAKPOINT} {
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    height: auto;
    max-width: 20rem;
    gap: 1.5rem;
    padding-top: 2.5rem;
  }
`;

const StatValue = styled.p<{ color: string }>`
  color: ${(p) => p.color};
  font-size: 3.125rem;
  font-weight: 700;
  text-align: center;
`;

const Description = styled.div<{
  marginLeft?: string;
  marginRight?: string;
  weight?: boolean;
  maxWidth?: string;
  locale: string;
  textColor: string;
  align: string;
  fontSize?: string;
}>`
  color: ${(p) => p.textColor};
  font-size: ${(p) => p.fontSize || "1rem"};
  font-weight: ${(p) => (p.weight ? 400 : 700)};
  letter-spacing: 0.075rem;
  margin-left: ${(p) => (p.marginLeft ? p.marginLeft : "1.5rem")};
  margin-right: ${(p) => p.marginRight};
  max-width: ${(p) => (p.maxWidth ? p.maxWidth : "9.375rem")};

  ${TIGHT_MOBILE_BREAKPOINT} {
    margin: 0;
    text-align: ${(p) => (p.locale === "jp" ? "left" : "center")};
  }
`;

const ToolTipWrap = styled.span`
  position: relative;
  display: inline;
`;

export default StatsBarItem;
